import React from "react";

import { useFeatureFlag, useGetCarbonProgramByFarmId, useNavigate, useTranslation, useUserContext } from "utils";

import { COVER_CROP_MIXER_ROUTE, FEATURE_FLAGS, TERRA_ROUTE } from "consts";

import { Button, Flex, Icon, OutlinedCard, Tag, TagColour, Typography } from "components";

import { SoilSightPromoCard } from "pages/app/home/components/SoilSightPromoCard";

const ICON_COLOR = "#24408a"; // blue-800

export const HomePagePlanningList = () => {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const { user } = useUserContext();

  const { data: carbonProgram } = useGetCarbonProgramByFarmId(user?.farm?.id ?? undefined);

  const { isEnabled: isSoilSightEnabled } = useFeatureFlag(FEATURE_FLAGS.SOIL_SIGHT_INTEGRATION);
  const { isEnabled: isCopilotEnabled } = useFeatureFlag(FEATURE_FLAGS.COPILOT);

  if (!user?.farm || (carbonProgram && !carbonProgram.platformFeatures.planning)) {
    return null;
  }

  const showTerraCard = isCopilotEnabled;
  const showCropMixComposerCard = carbonProgram?.platformFeatures.planning?.cropMixComposer !== false;

  const contentCards = [
    ...(showTerraCard
      ? [
          {
            key: "terra",
            icon: "klim/magic",
            title: t("planningCard.terra.title"),
            description: t("planningCard.terra.description"),
            tagText: t("planningCard.terra.tagText"),
            tagColor: "yellow" as TagColour,
            buttonText: t("planningCard.terra.button"),
            onClick: () => navigate(TERRA_ROUTE),
          },
        ]
      : []),
    ...(showCropMixComposerCard
      ? [
          {
            key: "coverCropMix",
            icon: "klim/plant-ai",
            title: t("planningCard.coverCropMix.title"),
            description: t("planningCard.coverCropMix.description"),
            tagText: t("planningCard.coverCropMix.tagText"),
            tagColor: "blue" as TagColour,
            buttonText: t("planningCard.coverCropMix.button"),
            onClick: () => navigate(COVER_CROP_MIXER_ROUTE),
          },
        ]
      : []),
  ];

  if (contentCards.length === 0 && isSoilSightEnabled) return null;

  return (
    <Flex vertical gap="2rem" className="homepage-planning-cards-container">
      <Typography variant="h2" className="homepage-planning-cards-title">
        {t("planningCard.title")}
      </Typography>
      {isSoilSightEnabled && <SoilSightPromoCard />}
      <Flex className="homepage-planning-card" gap="1rem" flex={1} align="stretch" wrap="nowrap">
        {contentCards.map((card) => (
          <OutlinedCard dataTestId="home-planning-card" className="card-item" key={card.key}>
            <Flex vertical gap="1.5rem" className="card-content">
              <Flex justify="space-between" align="start">
                <Flex justify="center" align="center" className="icon-wrapper">
                  <Icon iconName={card.icon} size="l" className="content-card-icon" color={ICON_COLOR} strokeWidth={1} />
                </Flex>
                <Tag text={card.tagText} colour={card.tagColor} />
              </Flex>
              <Flex vertical gap="0.75rem">
                <Typography variant="h2">{card.title}</Typography>
                <Typography variant="paragraph-m">{card.description}</Typography>
              </Flex>
              <div className="home-planning-card-footer">
                <Button type="primary" size="middle" onClick={card.onClick}>
                  {card.buttonText}
                </Button>
              </div>
            </Flex>
          </OutlinedCard>
        ))}
      </Flex>
    </Flex>
  );
};
